import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, AlertTriangle, Search } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  AttestationRow,
  AttestationRowSkeleton,
} from '@/components/attestations/AttestationRow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/auth';
import { api } from '@/lib/api';
import type { AttestationRecord } from '@/types/api';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'overdue', label: 'Overdue' },
] as const;

const STATUS_ORDER: Record<string, number> = {
  overdue: 0,
  pending: 1,
  acknowledged: 2,
};

const EMPTY_MESSAGES: Record<string, { title: string; description: string }> = {
  all: { title: 'No attestations yet', description: 'Attestations will appear here when policies are published.' },
  pending: { title: 'No pending attestations', description: 'All employees have responded.' },
  acknowledged: { title: 'All caught up', description: 'No acknowledged attestations to show.' },
  overdue: { title: 'No overdue attestations', description: 'Great — everyone is on time.' },
};

export function AttestationsPage() {
  const user = useAuthStore((s) => s.user);
  const isDepartmentRep = user?.role === UserRole.DEPARTMENT_REP;

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Single query: fetch all attestations, filter client-side
  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['attestations', 'all'],
    queryFn: () => api.attestations.list({}) as unknown as Promise<{ attestations: AttestationRecord[]; total: number }>,
    enabled: !!user,
    staleTime: 60_000,
  });

  const allAttestations = response?.attestations ?? [];

  // Derive status counts from the full list
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allAttestations.length };
    for (const a of allAttestations) {
      counts[a.status] = (counts[a.status] ?? 0) + 1;
    }
    return counts;
  }, [allAttestations]);

  const overdueCount = statusCounts.overdue ?? 0;

  // Client-side filtering by status, department, search
  const filtered = useMemo(() => {
    let result = [...allAttestations];

    if (selectedStatus !== 'all') {
      result = result.filter((a) => a.status === selectedStatus);
    }

    if (selectedDepartment !== 'all') {
      result = result.filter((a) => a.department === selectedDepartment);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.recipientName.toLowerCase().includes(q) ||
          a.documentTitle.toLowerCase().includes(q),
      );
    }

    result.sort(
      (a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3),
    );

    return result;
  }, [allAttestations, selectedStatus, selectedDepartment, searchQuery]);

  const departments = useMemo(() => {
    const depts = new Set(allAttestations.map((a: AttestationRecord) => a.department));
    return Array.from(depts).sort();
  }, [allAttestations]);

  const emptyMsg = EMPTY_MESSAGES[selectedStatus] ?? EMPTY_MESSAGES.all;

  if (!user || isLoading) {
    return (
      <div className="space-y-4 p-6">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-card" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Attestations"
        subtitle="Policy acknowledgment tracking"
      />

      {/* Overdue Alert */}
      {overdueCount > 0 && selectedStatus !== 'overdue' && (
        <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-300">
              {overdueCount} overdue attestation{overdueCount !== 1 ? 's' : ''}
            </p>
            <p className="mt-0.5 text-sm text-red-300/80">
              These employees have not acknowledged required policy updates.
              Consider sending reminders.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-red-500/30 text-red-400 hover:bg-red-500/10"
            onClick={() => setSelectedStatus('overdue')}
          >
            View Overdue
          </Button>
        </div>
      )}

      {/* Stats Row */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            {allAttestations.length}
          </span>{' '}
          total
        </span>
        <span className="text-sm text-amber-400">
          <span className="font-semibold">{statusCounts.pending ?? 0}</span>{' '}
          pending
        </span>
        <span className="text-sm text-green-400">
          <span className="font-semibold">
            {statusCounts.acknowledged ?? 0}
          </span>{' '}
          acknowledged
        </span>
        <span className="text-sm text-red-400">
          <span className="font-semibold">{statusCounts.overdue ?? 0}</span>{' '}
          overdue
        </span>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={selectedStatus}
          onValueChange={(v) => setSelectedStatus(v as string)}
        >
          <TabsList variant="line">
            {STATUS_OPTIONS.map((opt) => (
              <TabsTrigger key={opt.value} value={opt.value}>
                {opt.label}
                {(statusCounts[opt.value] ?? 0) > 0 && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                    {statusCounts[opt.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-1 items-center justify-end gap-3">
          {!isDepartmentRep && (
            <Select
              value={selectedDepartment}
              onValueChange={(v) => setSelectedDepartment(v ?? 'all')}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="relative w-64">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by employee or document"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 pl-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Attestation List */}
      <div className="rounded-lg border border-border">
        {/* Table header */}
        <div className="flex items-center gap-4 border-b border-border bg-muted/30 px-4 py-2">
          <div className="w-48 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Employee
          </div>
          <div className="flex-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Document
          </div>
          <div className="w-36 shrink-0 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Due Date
          </div>
          <div className="w-32 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Status
          </div>
          <div className="w-28 shrink-0 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Actions
          </div>
        </div>

        {isLoading ? (
          Array.from({ length: 8 }, (_, i) => <AttestationRowSkeleton key={i} />)
        ) : isError ? (
          <div className="p-8">
            <EmptyState
              icon={CheckSquare}
              title="Failed to load attestations"
              description="Something went wrong. Please try again."
              action={
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={CheckSquare}
              title={emptyMsg.title}
              description={emptyMsg.description}
            />
          </div>
        ) : (
          filtered.map((attestation) => (
            <AttestationRow
              key={attestation.attestationId}
              attestation={attestation}
            />
          ))
        )}
      </div>
    </div>
  );
}
